// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createTheme, type Components, type Theme } from "@mui/material/styles";
import type { CSSObject } from "@mui/system";

export enum ThemeMode {
  Light = "light",
  Dark = "dark",
}

// WSO2 brand palette
const Brand = {
  // Neutrals
  black: "#000000",
  gray900: "#0D0D0D",
  gray700: "#262626",
  gray500: "#565656",
  gray200: "#D9D9D9",
  gray100: "#F2F2F2",
  white: "#FFFFFF",
  // Navy blues
  navyDeep: "#17223A",
  navy: "#1B2A49",
  // Light blues
  blueLight: "#5CD1FF",
  bluePale: "#B7E4FC",
  // Oranges / reds
  orange: "#FF6700",
  orangeDark: "#F14E23",
};

// ---------------------------------------------------------------------------
// Shared non-colour config (identical across both themes)
// ---------------------------------------------------------------------------
const sharedShape = { borderRadius: 8 };

const sharedTypography = {
  fontFamily: "Montserrat, sans-serif",
  h6: { fontWeight: 700, letterSpacing: "-0.015em" },
  subtitle1: { fontWeight: 600 },
  subtitle2: { fontWeight: 600 },
  body2: { fontSize: "0.8125rem" },
  caption: { fontSize: "0.75rem", letterSpacing: "0.02em" },
};

// ---------------------------------------------------------------------------
// Component overrides factory — only the colour tokens differ per theme
// ---------------------------------------------------------------------------
interface ComponentTokens {
  // Card
  cardRoot: CSSObject;
  // Inputs
  inputBg: string;
  inputHoverBorder: string;
  inputFocusBorder: string;
  inputNotchedBorder: string;
  inputFocusShadow?: string;
  // Table
  tableHeadColor: string;
  tableHeadBorder: string;
  tableRowHoverBg: string;
  tableCellBorder: string;
  tableCellColor: string;
  // Tooltip
  tooltipBg: string;
  tooltipColor?: string;
  tooltipShadow: string;
  // Pagination
  paginationBorderTop: string;
  paginationSelected?: {
    backgroundColor: string;
    color: string;
    "&:hover": { backgroundColor: string };
  };
  // Modal
  modalBackdropBg: string;
  // Dropdowns (Autocomplete + Menu)
  dropdownShadow: string;
  dropdownBorder: string;
  dropdownBg?: string;
}

const buildComponents = (t: ComponentTokens): Components<Omit<Theme, "components">> => ({
  MuiCard: {
    styleOverrides: { root: t.cardRoot },
  },
  MuiAppBar: {
    styleOverrides: { root: { backgroundImage: "none" } },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: "0.01em",
        borderRadius: "6px",
      },
      outlined: {
        borderWidth: "1.5px",
        "&:hover": { borderWidth: "1.5px" },
      },
      sizeSmall: { fontSize: "0.8rem", padding: "5px 12px" },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: "8px",
        backgroundColor: t.inputBg,
        transition: "box-shadow 0.2s ease",
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: t.inputHoverBorder,
        },
        ...(t.inputFocusShadow && {
          "&.Mui-focused": { boxShadow: `0 0 0 3px ${t.inputFocusShadow}` },
        }),
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: t.inputFocusBorder,
          borderWidth: "1.5px",
        },
      },
      notchedOutline: { borderColor: t.inputNotchedBorder },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": { backgroundColor: t.inputBg },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: "0.875rem", fontWeight: 500 },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: { boxShadow: "none", border: "none", borderRadius: 0 },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        "& .MuiTableCell-head": {
          backgroundColor: "transparent",
          fontWeight: 700,
          fontSize: "0.6875rem",
          color: t.tableHeadColor,
          borderBottom: `1px solid ${t.tableHeadBorder}`,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          padding: "10px 16px",
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&:hover td": { backgroundColor: t.tableRowHoverBg },
        "&:last-child td": { borderBottom: 0 },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${t.tableCellBorder}`,
        fontSize: "0.875rem",
        padding: "11px 16px",
        color: t.tableCellColor,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { fontWeight: 600, fontSize: "0.75rem", height: "24px", borderRadius: "6px" },
      label: { paddingLeft: "9px", paddingRight: "9px" },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: "6px",
        fontSize: "0.75rem",
        fontFamily: "Montserrat",
        backgroundColor: t.tooltipBg,
        ...(t.tooltipColor && { color: t.tooltipColor }),
        padding: "6px 12px",
        boxShadow: t.tooltipShadow,
      },
      arrow: { color: t.tooltipBg },
    },
  },
  MuiPagination: {
    styleOverrides: {
      root: {
        "& .MuiPaginationItem-root": {
          borderRadius: "6px",
          fontWeight: 600,
          fontSize: "0.8125rem",
        },
        ...(t.paginationSelected && {
          "& .MuiPaginationItem-root.Mui-selected": t.paginationSelected,
        }),
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        boxShadow: t.dropdownShadow,
        borderRadius: "8px",
        border: `1px solid ${t.dropdownBorder}`,
        ...(t.dropdownBg && { backgroundColor: t.dropdownBg }),
      },
      listbox: {
        padding: "6px",
        "& .MuiAutocomplete-option": {
          borderRadius: "6px",
          marginBottom: "2px",
          fontSize: "0.875rem",
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        boxShadow: t.dropdownShadow,
        borderRadius: "8px",
        border: `1px solid ${t.dropdownBorder}`,
        ...(t.dropdownBg && { backgroundColor: t.dropdownBg }),
      },
      list: {
        padding: "6px",
        "& .MuiMenuItem-root": {
          borderRadius: "6px",
          fontSize: "0.875rem",
          marginBottom: "2px",
        },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: { borderTop: `1px solid ${t.paginationBorderTop}`, fontSize: "0.8125rem" },
      selectLabel: { fontSize: "0.8125rem" },
      displayedRows: { fontSize: "0.8125rem" },
    },
  },
  MuiSkeleton: {
    styleOverrides: { root: { borderRadius: "8px" } },
  },
  MuiModal: {
    styleOverrides: {
      backdrop: {
        backgroundColor: t.modalBackdropBg,
        backdropFilter: "blur(4px)",
      },
    },
  },
});

// ---------------------------------------------------------------------------
// Light theme
// ---------------------------------------------------------------------------
export const lightTheme = createTheme({
  palette: {
    mode: ThemeMode.Light,
    background: {
      default: Brand.gray100,
      paper: Brand.white,
    },
    primary: {
      main: Brand.navy,
      light: Brand.bluePale,
      dark: Brand.navyDeep,
      contrastText: Brand.white,
    },
    secondary: {
      main: Brand.orange,
      light: "#FF8C3A",
      dark: Brand.orangeDark,
      contrastText: Brand.white,
    },
    text: {
      primary: Brand.gray900,
      secondary: Brand.gray500,
      disabled: Brand.gray200,
    },
    error: {
      main: Brand.orangeDark,
      light: "#F98B78",
      dark: "#C73B19",
      contrastText: Brand.white,
    },
    warning: {
      main: Brand.orange,
      light: "#FF8C3A",
      dark: Brand.orangeDark,
      contrastText: Brand.white,
    },
    info: {
      main: Brand.blueLight,
      light: Brand.bluePale,
      dark: "#2BA8D8",
      contrastText: Brand.gray900,
    },
    success: {
      main: "#22c55e",
      light: "#86efac",
      dark: "#15803d",
      contrastText: Brand.white,
    },
    divider: "rgba(38, 38, 38, 0.1)",
  },
  shape: sharedShape,
  typography: sharedTypography,
  components: buildComponents({
    cardRoot: {
      boxShadow: "none",
      backgroundImage: "none",
      border: "1px solid rgba(86, 86, 86, 0.15)",
    },
    inputBg: Brand.white,
    inputHoverBorder: Brand.navy,
    inputFocusBorder: Brand.navy,
    inputNotchedBorder: "rgba(86, 86, 86, 0.2)",
    tableHeadColor: Brand.gray500,
    tableHeadBorder: "rgba(86, 86, 86, 0.15)",
    tableRowHoverBg: "rgba(86, 86, 86, 0.04)",
    tableCellBorder: "rgba(86, 86, 86, 0.08)",
    tableCellColor: Brand.gray900,
    tooltipBg: Brand.gray700,
    tooltipColor: Brand.gray100,
    tooltipShadow: "0 4px 12px rgba(0,0,0,0.15)",
    paginationBorderTop: "rgba(86, 86, 86, 0.1)",
    paginationSelected: {
      backgroundColor: Brand.orange,
      color: Brand.white,
      "&:hover": { backgroundColor: Brand.orangeDark },
    },
    modalBackdropBg: "rgba(13, 13, 13, 0.6)",
    dropdownShadow: "0 8px 24px rgba(27, 42, 73, 0.14), 0 2px 6px rgba(27, 42, 73, 0.08)",
    dropdownBorder: "rgba(86, 86, 86, 0.12)",
  }),
});

// ---------------------------------------------------------------------------
// Dark theme
// ---------------------------------------------------------------------------
export const darkTheme = createTheme({
  palette: {
    mode: ThemeMode.Dark,
    background: {
      default: "#111111",
      paper: "#1C1C1C",
    },
    primary: {
      main: Brand.orange,
      light: Brand.blueLight,
      dark: Brand.orangeDark,
      contrastText: Brand.white,
    },
    secondary: {
      main: "#141414",
      light: "#1C1C1C",
      dark: "#0D0D0D",
      contrastText: Brand.white,
    },
    text: {
      primary: Brand.gray100,
      secondary: Brand.gray200,
      disabled: Brand.gray500,
    },
    error: {
      main: Brand.orangeDark,
      light: "#F98B78",
      dark: "#C73B19",
      contrastText: Brand.white,
    },
    warning: {
      main: Brand.orange,
      light: "#FF8C3A",
      dark: Brand.orangeDark,
      contrastText: Brand.white,
    },
    info: {
      main: Brand.blueLight,
      light: Brand.bluePale,
      dark: "#2BA8D8",
      contrastText: Brand.gray900,
    },
    success: {
      main: "#4ade80",
      light: "#86efac",
      dark: "#22c55e",
      contrastText: Brand.white,
    },
    divider: "rgba(242, 242, 242, 0.08)",
  },
  shape: sharedShape,
  typography: sharedTypography,
  components: buildComponents({
    cardRoot: {
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.35), 0 1px 2px rgba(0, 0, 0, 0.25)",
      transition: "box-shadow 0.25s ease",
      backgroundImage: "none",
      "&:hover": {
        boxShadow: "0 14px 32px rgba(0, 0, 0, 0.5), 0 4px 10px rgba(0, 0, 0, 0.35)",
      },
    },
    inputBg: "#111111",
    inputHoverBorder: "rgba(242, 242, 242, 0.3)",
    inputFocusBorder: Brand.orange,
    inputNotchedBorder: "rgba(242, 242, 242, 0.12)",
    inputFocusShadow: "rgba(255, 103, 0, 0.2)",
    tableHeadColor: Brand.gray200,
    tableHeadBorder: "rgba(242, 242, 242, 0.1)",
    tableRowHoverBg: "rgba(242, 242, 242, 0.04)",
    tableCellBorder: "rgba(242, 242, 242, 0.07)",
    tableCellColor: Brand.gray100,
    tooltipBg: "#2A2A2A",
    tooltipShadow: "0 4px 12px rgba(0,0,0,0.4)",
    paginationBorderTop: "rgba(242, 242, 242, 0.07)",
    paginationSelected: {
      backgroundColor: Brand.orange,
      color: Brand.white,
      "&:hover": { backgroundColor: Brand.orangeDark },
    },
    modalBackdropBg: "rgba(0, 0, 0, 0.8)",
    dropdownShadow: "0 8px 24px rgba(0, 0, 0, 0.6)",
    dropdownBorder: "rgba(242, 242, 242, 0.08)",
    dropdownBg: "#252525",
  }),
});
